import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { GroupBadge } from "@/components/GroupBadge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Search, UploadCloud, X, PlaneTakeoff, PlaneLanding, Users, DollarSign, Plane,
  CircleCheck, Clock, TriangleAlert, HelpCircle, History, ChevronRight, ShieldCheck,
} from "lucide-react";

const CAT_NAME = { pc_winner: "PC Winner", mdc_winner: "MDC Winner", non_pc: "Non-PC", sprint_to_pc: "Sprint to PC" };

const STATUS_STYLES = {
  "Complete · Navan": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Outside Invite": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Outside Navan": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Return self-arranged": "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  "Booking Pending": "bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(38_50%_34%)]",
};
const TRANSFER_BADGE = {
  "Ready": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Self-arranged": "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  "Missing Details": "bg-[hsl(38_80%_90%)] text-[hsl(38_50%_35%)]",
};

const FILTERS = [
  ["All", () => true],
  ["Complete", (r) => String(r.travel_status).startsWith("Complete")],
  ["Booking Pending", (r) => r.travel_status === "Booking Pending"],
  ["Navan Invite", (r) => r.flight_source === "Navan Invite"],
  ["Outside Invite", (r) => r.flight_source === "Booked Outside Invite"],
  ["Outside Navan", (r) => r.flight_source === "Booked Outside Navan"],
  ["Self-arranged", (r) => r.travel_status === "Complete · Return self-arranged"],
  ["Has Guest", (r) => !!r.guest],
  ["Exceptions", (r) => r.has_exception],
];

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const dstr = (v) => (v == null ? "—" : String(v).slice(0, 10));

const Badge = ({ cls, testid, children }) => (
  <span data-testid={testid} className={`text-[10px] font-semibold rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${cls || "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{children}</span>
);

function Seg({ icon: Icon, date, time, flight, sub }) {
  if (!date && !flight) return <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>;
  return (
    <div className="leading-tight">
      <div className="text-[13px] text-[hsl(var(--foreground))] flex items-center gap-1">
        <Icon className="h-3 w-3 text-[hsl(var(--secondary))]" />
        <span className="tabular-nums">{dstr(date)}</span>{time ? <span className="tabular-nums text-[hsl(var(--muted-foreground))]">· {String(time).slice(0, 5)}</span> : null}
      </div>
      <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{sub}{flight ? ` · ${flight}` : ""}</div>
    </div>
  );
}

export default function Travel() {
  const { eventId } = useAdminEvent();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [q, setQ] = useState("");
  const [view, setView] = useState("All");
  const [rsvpF, setRsvpF] = useState("all");
  const [catF, setCatF] = useState("all");
  const [tab, setTab] = useState("table");
  const [selected, setSelected] = useState(null);

  const load = () => {
    if (!eventId) return;
    setLoadState("loading");
    api.travelCanonical(eventId)
      .then((t) => { setData(t); setLoadState("ok"); })
      .catch(() => setLoadState("error"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const rows = data?.rows || [];
  const k = data?.kpis || {};

  const viewPred = FILTERS.find((v) => v[0] === view)?.[1] || (() => true);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (!viewPred(r)) return false;
      if (rsvpF !== "all" && r.rsvp !== rsvpF) return false;
      if (catF !== "all" && r.category !== catF) return false;
      if (term && !(`${r.attendee} ${r.email}`.toLowerCase().includes(term))) return false;
      return true;
    });
  }, [rows, q, view, rsvpF, catF, viewPred]);

  const goImport = () => navigate("/manager/flight-reconciliation?import=1");

  return (
    <div data-testid="manager-travel-view">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Travel Operations</h2>
          <PageIntro>
            Operational travel dashboard powered by the <strong>canonical reconciliation state</strong>. This is
            the single source of truth for travel — import &amp; reconcile new flight sources with the action on the
            right, which opens the reconciliation workflow and returns you here once applied.
          </PageIntro>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="manager-travel-import-button" onClick={goImport} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-[opacity]">
            <UploadCloud className="h-4 w-4" /> Import / Reconcile Flight Sources
          </button>
        </div>
      </div>

      {data && !data.canonical_present && (
        <div data-testid="travel-empty-canonical" className="premium-card p-4 mb-4 text-sm text-[hsl(38_50%_34%)] bg-[hsl(var(--pc-gold)/0.08)]">
          {data.note}
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
        <Kpi testid="kpi-active" icon={Users} label="Active travelers" value={k.travel?.active} />
        <Kpi testid="kpi-complete" icon={CircleCheck} label="Travel complete" value={k.travel?.complete} tone="good" />
        <Kpi testid="kpi-pending" icon={Clock} label="Booking pending" value={k.travel?.booking_pending} tone="warn" />
        <Kpi testid="kpi-navan-invite" icon={Plane} label="Navan Invite" value={k.source?.navan_invite} />
        <Kpi testid="kpi-outside-invite" icon={Plane} label="Outside Invite" value={k.source?.outside_invite} />
        <Kpi testid="kpi-outside-navan" icon={Plane} label="Outside Navan" value={k.source?.outside_navan} />
        <Kpi testid="kpi-arr-ready" icon={PlaneLanding} label="Transfer-ready arrivals" value={k.transfer?.ready_arrivals} />
        <Kpi testid="kpi-dep-ready" icon={PlaneTakeoff} label="Transfer-ready departures" value={k.transfer?.ready_departures} />
        <Kpi testid="kpi-exceptions" icon={TriangleAlert} label="Exceptions" value={k.exceptions_open} tone={k.exceptions_open ? "bad" : undefined} />
        <Kpi testid="kpi-navan-airfare" icon={DollarSign} label="Navan airfare" value={money(k.managed_navan_airfare?.amount)} />
        <Kpi testid="kpi-external-airfare" icon={DollarSign} label="External airfare" value={money(k.master_external_fares?.amount)} />
        <Kpi testid="kpi-total-airfare" icon={DollarSign} label="Total captured airfare" value={money(k.total_captured_airfare?.amount)} tone="good" hint={`${k.total_captured_airfare?.count ?? 0} fares`} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-3">
        <TabBtn active={tab === "table"} onClick={() => setTab("table")} testid="travel-tab-table">Traveler Table</TabBtn>
        <TabBtn active={tab === "actions"} onClick={() => setTab("actions")} testid="travel-tab-actions">
          Action Center {(data?.booking_pending_list?.length || 0) + (data?.exceptions?.length || 0) > 0 ? <span className="ml-1 text-[10px] rounded-full px-1.5 py-0.5 bg-[hsl(var(--pc-gold)/0.2)] text-[hsl(38_50%_34%)]">{(data?.booking_pending_list?.length || 0) + (data?.exceptions?.length || 0)}</span> : null}
        </TabBtn>
      </div>

      {tab === "table" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input data-testid="manager-travel-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9" />
            </div>
            <PillGroup label="RSVP" value={rsvpF} onChange={setRsvpF} testidPrefix="rsvp" options={[["all", "All"], ["attending", "Attending"], ["pending", "Pending"]]} />
            <PillGroup label="Category" value={catF} onChange={setCatF} testidPrefix="cat" options={[["all", "All"], ["pc_winner", "PC"], ["mdc_winner", "MDC"], ["non_pc", "Non-PC"]]} />
          </div>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-1 mb-3">
            {FILTERS.map(([label, pred]) => {
              const count = rows.filter(pred).length;
              const active = view === label;
              return (
                <button key={label} data-testid={`manager-travel-view-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setView(label)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted)/0.7)]"}`}>
                  {label} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="premium-card overflow-hidden">
            <div className="hidden xl:grid grid-cols-[2fr_0.9fr_0.8fr_1.4fr_0.9fr_1.3fr_1.3fr_0.6fr_0.9fr_1.2fr_0.6fr] gap-2 px-4 py-2.5 border-b border-[hsl(var(--border))] text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]">
              <div>Attendee</div><div>Category</div><div>RSVP</div><div>Travel Status</div><div>Source</div>
              <div>Arrival SJD</div><div>Departure SJD</div><div>Guest</div><div className="text-right">Airfare</div><div>Transfer</div><div className="text-right">Flags</div>
            </div>
            {loadState === "loading" && <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Loading travel…</div>}
            {loadState === "error" && <div className="p-8 text-center text-sm text-[hsl(0_60%_45%)]">Failed to load travel data.</div>}
            {loadState === "ok" && filtered.length === 0 && <div data-testid="manager-travel-empty" className="p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">No attendees match these filters.</div>}
            {loadState === "ok" && filtered.map((r) => (
              <button key={r.email} data-testid="manager-travel-row" onClick={() => setSelected(r)}
                className="w-full text-left grid grid-cols-1 xl:grid-cols-[2fr_0.9fr_0.8fr_1.4fr_0.9fr_1.3fr_1.3fr_0.6fr_0.9fr_1.2fr_0.6fr] gap-2 px-4 py-3 border-b border-[hsl(var(--border))] items-center hover:bg-[hsl(var(--muted)/0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] transition-colors">
                <div className="min-w-0">
                  <div data-testid="manager-travel-attendee-name" className="text-sm font-semibold text-[hsl(var(--primary))] truncate">{r.attendee}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{r.email}</div>
                </div>
                <div>{r.category ? <GroupBadge groupId={r.category} groupName={CAT_NAME[r.category] || r.category} size="xs" /> : <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{r.rsvp_display}</div>
                <div><Badge cls={STATUS_STYLES[r.travel_status]} testid="travel-status-badge">{r.travel_status}</Badge></div>
                <div className="text-[11px] font-medium text-[hsl(var(--secondary))]">{r.flight_source}</div>
                <div><Seg icon={PlaneLanding} date={r.arr_date} time={r.arr_time} flight={r.arr_flight} sub={r.arr_origin ? `${r.arr_origin}→${r.arr_airport || "SJD"}` : (r.arr_airport || "")} /></div>
                <div>{r.dep_self_arranged ? <span className="text-xs italic text-[hsl(var(--secondary))]">Self-arranged</span> : <Seg icon={PlaneTakeoff} date={r.dep_date} time={r.dep_time} flight={r.dep_flight} sub={r.dep_final ? `SJD→${r.dep_final}` : "SJD"} />}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{r.guest ? "Yes" : "—"}</div>
                <div className="text-xs text-right tabular-nums text-[hsl(var(--foreground))]">{money(r.airfare)}</div>
                <div className="flex flex-col gap-1">
                  <Badge cls={TRANSFER_BADGE[r.arrival_transfer]}>A: {r.arrival_transfer}</Badge>
                  <Badge cls={TRANSFER_BADGE[r.departure_transfer]}>D: {r.departure_transfer}</Badge>
                </div>
                <div className="flex items-center justify-end gap-1">
                  {r.has_exception && <TriangleAlert data-testid="manager-travel-row-exception" className="h-4 w-4 text-[hsl(0_60%_45%)]" />}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {tab === "actions" && (
        <ActionCenter data={data} loadState={loadState} onOpen={(email) => { const r = rows.find((x) => x.email === email); if (r) setSelected(r); }} />
      )}

      <TravelDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone, testid }) {
  const toneCls = tone === "good" ? "text-[hsl(var(--primary))]" : tone === "warn" ? "text-[hsl(38_55%_38%)]" : tone === "bad" ? "text-[hsl(0_60%_45%)]" : "text-[hsl(var(--primary))]";
  return (
    <div data-testid={`manager-travel-${testid}`} className="premium-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`font-serif text-2xl mt-1 ${toneCls}`}>{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{hint}</div>}
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted)/0.7)]"}`}>{children}</button>
  );
}
function PillGroup({ label, value, onChange, options, testidPrefix }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--muted))] p-1">
      <span className="text-[10px] uppercase font-semibold text-[hsl(var(--muted-foreground))] px-2">{label}</span>
      {options.map(([v, l]) => (
        <button key={v} data-testid={`manager-travel-filter-${testidPrefix}-${v}`} onClick={() => onChange(v)}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${value === v ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"}`}>{l}</button>
      ))}
    </div>
  );
}

// --- Action Center (canonical-derived): booking pending, exceptions, historical
function ActionCenter({ data, loadState, onOpen }) {
  if (loadState === "loading") return <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>;
  const pending = data?.booking_pending_list || [];
  const exceptions = data?.exceptions || [];
  const historical = data?.historical_fares || [];
  return (
    <div data-testid="manager-travel-action-center" className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Booking Pending ({pending.length})</div>
        {pending.length === 0 ? <div className="premium-card p-6 text-center text-sm text-[hsl(var(--muted-foreground))]">No travelers pending a booking.</div> : (
          <div className="premium-card divide-y divide-[hsl(var(--border))]">
            {pending.map((p) => (
              <button key={p.email} data-testid="travel-pending-row" onClick={() => onOpen(p.email)} className="w-full text-left flex items-center justify-between px-4 py-2.5 hover:bg-[hsl(var(--muted)/0.4)]">
                <span className="text-sm font-medium text-[hsl(var(--primary))]">{p.attendee} <span className="text-[hsl(var(--muted-foreground))] font-normal">· {p.email}</span></span>
                <Badge cls={STATUS_STYLES["Booking Pending"]}>Booking Pending</Badge>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Approved Exceptions ({exceptions.length})</div>
        {exceptions.length === 0 ? <div className="premium-card p-6 text-center text-sm text-[hsl(var(--muted-foreground))]">No approved travel exceptions.</div> : (
          <div className="premium-card divide-y divide-[hsl(var(--border))]">
            {exceptions.map((e, i) => (
              <div key={i} data-testid="travel-exception-row" className="px-4 py-2.5">
                <div className="text-sm font-medium text-[hsl(var(--primary))]">{e.attendee} <span className="text-[hsl(var(--muted-foreground))] font-normal">· {e.type}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{e.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {historical.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><History className="h-4 w-4" /> Historical / Superseded ({historical.length})</div>
          <div className="premium-card divide-y divide-[hsl(var(--border))]">
            {historical.map((h, i) => (
              <div key={i} data-testid="travel-historical-row" className="px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm">{h.attendee} <span className="text-[hsl(var(--muted-foreground))]">· {h.source}</span></span>
                <span className="line-through text-sm text-[hsl(var(--muted-foreground))]">{money(h.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Detail drawer (informational — canonical) ------------------------------
function TravelDrawer({ row, onClose }) {
  if (!row) return null;
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="manager-travel-detail-drawer" className="w-full sm:max-w-xl overflow-y-auto no-scrollbar p-0 bg-[hsl(var(--background))]">
        <div className="p-5 border-b border-[hsl(var(--border))]">
          <div className="flex items-center justify-between">
            <div><div className="font-serif text-xl text-[hsl(var(--primary))]">{row.attendee}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{row.email}</div></div>
            <button aria-label="Close" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-[hsl(var(--muted)/0.6)]"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Badge cls={STATUS_STYLES[row.travel_status]}>{row.travel_status}</Badge>
            <span className="text-[11px] font-medium text-[hsl(var(--secondary))]">Source: {row.flight_source}</span>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {row.note && <div className="rounded-xl bg-[hsl(var(--pc-gold)/0.1)] p-3 text-sm text-[hsl(38_45%_30%)]">{row.note}</div>}

          <Section title="Traveler">
            <KV k="Category" v={CAT_NAME[row.category] || "—"} />
            <KV k="RSVP" v={row.rsvp_display} />
            <KV k="Guest / plus-one" v={row.guest ? "Yes" : "None"} />
          </Section>

          <Section title="Canonical itinerary">
            <KV k="Canonical source" v={row.canonical_source} />
            <KV k="Managed / self-arranged" v={row.managed_indicator} />
            <KV k="Booking reference" v={row.booking_ref || "—"} />
          </Section>

          <Section title="Arrival & departure">
            <div className="grid grid-cols-2 gap-3">
              <div className="premium-card p-3">
                <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1"><PlaneLanding className="h-3 w-3" /> Arrival into SJD</div>
                <div className="text-sm mt-1 tabular-nums">{dstr(row.arr_date)} {row.arr_time ? String(row.arr_time).slice(0, 5) : ""}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{row.arr_origin ? `${row.arr_origin} → ` : ""}{row.arr_airport || "SJD"}{row.arr_flight ? ` · ${row.arr_flight}` : ""}</div>
                <div className="mt-1"><Badge cls={TRANSFER_BADGE[row.arrival_transfer]}>Transfer: {row.arrival_transfer}</Badge></div>
              </div>
              <div className="premium-card p-3">
                <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1"><PlaneTakeoff className="h-3 w-3" /> Departure from SJD</div>
                {row.dep_self_arranged ? <div className="text-sm mt-1 italic text-[hsl(var(--secondary))]">Self-arranged (attendee-managed)</div> : (
                  <>
                    <div className="text-sm mt-1 tabular-nums">{dstr(row.dep_date)} {row.dep_time ? String(row.dep_time).slice(0, 5) : ""}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">SJD{row.dep_final ? ` → ${row.dep_final}` : ""}{row.dep_flight ? ` · ${row.dep_flight}` : ""}</div>
                  </>
                )}
                <div className="mt-1"><Badge cls={TRANSFER_BADGE[row.departure_transfer]}>Transfer: {row.departure_transfer}</Badge></div>
              </div>
            </div>
          </Section>

          <Section title="Airfare (Manager-only)">
            <KV k="Captured airfare" v={money(row.airfare)} />
            <KV k="Currency" v={row.currency || "—"} />
            <KV k="Fare source" v={row.fare_source || "—"} />
            <KV k="Fare status" v={row.fare_status} />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Canonical captured airfare — never exposed to attendees.</div>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (<div><div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] mb-2">{title}</div>{children}</div>);
}
function KV({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-[hsl(var(--border))]/60">
      <span className="text-xs text-[hsl(var(--muted-foreground))]">{k}</span>
      <span className="text-xs font-medium text-[hsl(var(--foreground))] text-right">{String(v ?? "—")}</span>
    </div>
  );
}
